/**
 * AccountSettings Component
 * Page for managing account settings including language and account deletion.
 * Language preference is persisted via API for cross-device sync.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Settings,
  AlertTriangle,
  Trash2,
  ArrowLeft,
  User,
  Shield,
  Loader2,
  CheckCircle2,
  Globe,
  Check,
  Smartphone,
} from 'lucide-react';
import {
  SUPPORTED_LANGUAGES,
  setLanguagePreference,
  getLanguagePreference,
  SupportedLanguageCode,
} from '@/i18n';
import { PageLayout } from '@/components';
import { UnauthenticatedPage } from '@/components/ui';
import { useAuthContext } from '@/contexts/AuthContext';
import { accountApi } from '@/services/accountApi';
import { settingsApi } from '@/services/settingsApi';
import { setAppModePreference } from '@/lib/appMode';
import { errorMessages } from '@/lib/errors';
import { createLogger } from '@/lib/logger';

const log = createLogger('AccountSettings');

export const AccountSettings: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, isLoading: authLoading, user, logout, userTier, appMode, setAppMode } = useAuthContext();

  // Language preference state
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguageCode>(
    getLanguagePreference() as SupportedLanguageCode
  );
  const [languageSaved, setLanguageSaved] = useState(false);

  // App Mode state
  const [appModeSaved, setAppModeSaved] = useState(false);

  // Delete account states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const CONFIRM_PHRASE = 'DELETE MY ACCOUNT';

  const handleLanguageChange = async (languageCode: SupportedLanguageCode) => {
    setSelectedLanguage(languageCode);
    setLanguagePreference(languageCode);
    setLanguageSaved(true);
    setTimeout(() => setLanguageSaved(false), 2000);

    // Persist to API for cross-device sync
    if (isAuthenticated) {
      try {
        await settingsApi.saveUserSettings({ language: languageCode });
      } catch (err) {
        log.error('Failed to save language to API', err instanceof Error ? err : new Error('Unknown'));
      }
    }
  };

  const handleAppModeToggle = async () => {
    const newValue = !appMode;
    setAppMode(newValue);
    setAppModePreference(newValue);
    setAppModeSaved(true);
    setTimeout(() => setAppModeSaved(false), 2000);

    // Persist to API for cross-device sync
    if (isAuthenticated) {
      try {
        await settingsApi.saveUserSettings({ appMode: newValue });
      } catch (err) {
        log.error('Failed to save App Mode to API', err instanceof Error ? err : new Error('Unknown'));
      }
    }

    // Redirect to /add when enabling App Mode
    if (newValue) {
      navigate('/add');
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmText !== CONFIRM_PHRASE) {
      setDeleteError(`Please type "${CONFIRM_PHRASE}" to confirm.`);
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await accountApi.deleteAccount();
      setDeleteSuccess(true);

      // Log the user out after a brief delay to show success message
      setTimeout(async () => {
        await logout();
        navigate('/', { replace: true });
      }, 2000);
    } catch (err) {
      setDeleteError(errorMessages.deleteAccount(err));
      setIsDeleting(false);
    }
  };

  // Loading state
  if (authLoading) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
        </div>
      </PageLayout>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <UnauthenticatedPage
        icon={<Settings className="w-8 h-8 text-slate-400" />}
        title={t('settings.accountSettings')}
        description={t('settings.signInRequired')}
      />
    );
  }

  // Deletion success screen
  if (deleteSuccess) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">{t('settings.accountDeleted')}</h2>
            <p className="text-slate-600 mb-6">
              {t('settings.accountDeletedMessage')}
            </p>
            <div className="flex justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="min-h-screen bg-slate-50 pt-5 pb-8 px-4 sm:pt-24">
        <div className="max-w-2xl mx-auto">

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <Settings className="w-7 h-7 text-indigo-600" />
              {t('settings.accountSettings')}
            </h1>
            <p className="text-slate-600 mt-1">
              {t('settings.managePreferences')}
            </p>
          </div>

          {/* Account Info Section */}
          <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <User className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">{t('settings.accountInformation')}</h2>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-600">{t('settings.email')}</span>
                <span className="text-slate-900 font-medium">{user?.email || t('settings.notAvailable')}</span>
              </div>
              {user?.fullName && (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">{t('settings.name')}</span>
                  <span className="text-slate-900 font-medium">{user.fullName}</span>
                </div>
              )}
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-600">{t('settings.plan')}</span>
                <span className="text-slate-900 font-medium capitalize">{userTier}</span>
              </div>
            </div>
          </section>

          {/* Language Preferences */}
          <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Globe className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-slate-900">{t('settings.languagePreferences')}</h2>
                {languageSaved && (
                  <span className="flex items-center gap-1 text-sm text-emerald-600">
                    <Check className="w-4 h-4" />
                    {t('settings.saved')}
                  </span>
                )}
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              {t('settings.languageDescription')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-colors ${
                    selectedLanguage === lang.code
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700'
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      selectedLanguage === lang.code
                        ? 'border-indigo-500'
                        : 'border-slate-300'
                    }`}
                  >
                    {selectedLanguage === lang.code && (
                      <div className="w-2 h-2 rounded-full bg-indigo-500" />
                    )}
                  </div>
                  <span className="text-sm font-medium">{lang.label}</span>
                </button>
              ))}
            </div>
          </section>

          {/* App Mode */}
          <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Smartphone className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-slate-900">{t('settings.appMode')}</h2>
                {appModeSaved && (
                  <span className="flex items-center gap-1 text-sm text-emerald-600">
                    <Check className="w-4 h-4" />
                    {t('settings.saved')}
                  </span>
                )}
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              {t('settings.appModeDescription')}
            </p>

            <div className="flex items-center gap-3">
              <button
                onClick={handleAppModeToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  appMode ? 'bg-indigo-600' : 'bg-slate-200'
                }`}
                aria-label={t('settings.appModeToggle')}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    appMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-slate-700">
                {appMode ? t('settings.appModeEnabled') : t('settings.appModeDisabled')}
              </span>
            </div>
          </section>

          {/* Danger Zone */}
          <section className="bg-white rounded-xl border border-red-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <Shield className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-red-900">{t('settings.dangerZone')}</h2>
            </div>

            <div className="p-4 bg-red-50 rounded-lg mb-4">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-red-800">
                  <p className="font-medium mb-1">{t('settings.permanentDeletion')}</p>
                  <p>
                    {t('settings.deletionWarning')}
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1 text-red-700">
                    <li>{t('settings.deletionItem1')}</li>
                    <li>{t('settings.deletionItem2')}</li>
                    <li>{t('settings.deletionItem3')}</li>
                    <li>{t('settings.deletionItem4')}</li>
                    <li>{t('settings.deletionItem5')}</li>
                    <li>{t('settings.deletionItem6')}</li>
                  </ul>
                  <p className="mt-2 font-medium">{t('settings.cannotBeUndone')}</p>
                </div>
              </div>
            </div>

            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 px-4 py-2.5 text-red-600 border border-red-300 rounded-lg
                         hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                {t('settings.deleteMyAccount')}
              </button>
            ) : (
              <div className="space-y-4">
                <div>
                  <label htmlFor="confirm-delete" className="block text-sm font-medium text-slate-700 mb-2">
                    Type <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{CONFIRM_PHRASE}</span> to confirm:
                  </label>
                  <input
                    id="confirm-delete"
                    type="text"
                    value={confirmText}
                    onChange={(e) => {
                      setConfirmText(e.target.value);
                      setDeleteError(null);
                    }}
                    placeholder={t('settings.typePlaceholder')}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    disabled={isDeleting}
                  />
                </div>

                {deleteError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {deleteError}
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setConfirmText('');
                      setDeleteError(null);
                    }}
                    className="px-4 py-2.5 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                    disabled={isDeleting}
                  >
                    {t('settings.cancel')}
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={isDeleting || confirmText !== CONFIRM_PHRASE}
                    className="flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg
                             hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDeleting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('settings.deletingAccount')}
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        {t('settings.permanentlyDelete')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </PageLayout>
  );
};

export default AccountSettings;
